const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api/v1';

const session = {
  get accessToken() { return sessionStorage.getItem('oneplay_billing_access_token'); },
  get refreshToken() { return sessionStorage.getItem('oneplay_billing_refresh_token'); },
  setTokens(data) {
    if (data.access_token) sessionStorage.setItem('oneplay_billing_access_token', data.access_token);
    if (data.refresh_token) sessionStorage.setItem('oneplay_billing_refresh_token', data.refresh_token);
  },
  clear() { sessionStorage.removeItem('oneplay_billing_access_token'); sessionStorage.removeItem('oneplay_billing_refresh_token'); },
};

let refreshing = null;

// Một lần làm mới token dùng chung cho mọi request và cho Socket.IO khi kết nối lại.
function refreshSession() {
  if (!session.refreshToken) return Promise.resolve(false);
  refreshing ||= fetch(`${API_URL}/auth/refresh`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ refresh_token: session.refreshToken }) })
    .then(async (response) => {
      if (!response.ok) { session.clear(); return false; }
      session.setTokens(await response.json());
      return true;
    })
    .catch(() => false)
    .finally(() => { refreshing = null; });
  return refreshing;
}

async function authorizedFetch(path, options = {}, canRefresh = true) {
  const headers = new Headers(options.headers || {});
  if (!headers.has('Content-Type') && options.body && !(options.body instanceof FormData)) headers.set('Content-Type', 'application/json');
  if (session.accessToken) headers.set('Authorization', `Bearer ${session.accessToken}`);
  const activeStore = sessionStorage.getItem('oneplay_billing_store');
  if (activeStore && !headers.has('X-Store-Id')) headers.set('X-Store-Id', String(activeStore));
  const response = await fetch(`${API_URL}${path}`, { ...options, headers });
  if (response.status === 401 && canRefresh && session.refreshToken && path !== '/auth/refresh' && await refreshSession()) {
    return authorizedFetch(path, options, false);
  }
  return response;
}

async function failure(response) {
  const data = await response.json().catch(() => ({}));
  const error = new Error(data.error?.message || 'Không thể xử lý yêu cầu');
  error.code = data.error?.code;
  error.details = data.error?.details;
  error.status = response.status;
  return error;
}

async function request(path, options = {}, canRefresh = true) {
  let response;
  try {
    response = await authorizedFetch(path, options, canRefresh);
  } catch {
    throw new Error('Không kết nối được máy chủ. Kiểm tra mạng rồi thử lại.');
  }
  if (response.status === 204) return null;
  if (!response.ok) throw await failure(response);
  return response.json().catch(() => ({}));
}

async function download(path, filename) {
  const response = await authorizedFetch(path);
  if (!response.ok) throw await failure(response);
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export const api = {
  get: (path) => request(path),
  download,
  refresh: refreshSession,
  post: (path, body, idempotent = false) => request(path, { method: 'POST', headers: idempotent ? { 'Idempotency-Key': crypto.randomUUID() } : {}, body: JSON.stringify(body) }),
  postForm: (path, body) => request(path, { method: 'POST', body }),
  put: (path, body) => request(path, { method: 'PUT', body: JSON.stringify(body) }),
  putForm: (path, body) => request(path, { method: 'PUT', body }),
  patch: (path, body) => request(path, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: (path, body, idempotent = false) => request(path, { method: 'DELETE', headers: idempotent ? { 'Idempotency-Key': crypto.randomUUID() } : {}, body: body ? JSON.stringify(body) : undefined }),
  login: async (email, password) => { const data = await request('/auth/login', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, password }) }, false); session.setTokens(data); return data; },
  // Nhận phiên chuyển từ trang Billing chính (mã dùng một lần trong #handoff=...)
  redeemHandoff: async (code) => { const data = await request('/auth/handoff/redeem', { method: 'POST', body: JSON.stringify({ code }) }, false); session.setTokens(data); return data; },
  logout: async () => { try { await request('/auth/logout', { method: 'POST', body: JSON.stringify({ refresh_token: session.refreshToken }) }, false); } finally { session.clear(); } },
};

export { API_URL, session };
